/**
 * useCowork — the desktop bridge ↔ CoWork store controller: mode switching,
 * opening/closing a local folder, autonomy + execution-tier selectors, machine
 * capabilities, and the change set. Desktop-only; in the web app `desktop` is
 * false and the actions are no-ops.
 *
 * @module hooks/use-cowork
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { getDesktop, isDesktopApp, type Autonomy, type DeviceCapabilities, type ExecTier } from "@/lib/desktop";
import { useCoworkStore } from "@/store/cowork-store";
import { getAuthHeader } from "@/utils/auth-storage";

export function useCowork() {
  const mode = useCoworkStore((s) => s.mode);
  const deviceId = useCoworkStore((s) => s.deviceId);
  const folderName = useCoworkStore((s) => s.folderName);
  const folderPath = useCoworkStore((s) => s.folderPath);
  const connected = useCoworkStore((s) => s.connected);
  const autonomy = useCoworkStore((s) => s.autonomy);
  const execTier = useCoworkStore((s) => s.execTier);
  const changes = useCoworkStore((s) => s.changes);
  const desktop = isDesktopApp();
  const [capabilities, setCapabilities] = useState<DeviceCapabilities | null>(null);
  const [recentFolders, setRecentFolders] = useState<string[]>([]);

  // Hydrate from the shell on mount, push the auth token, load capabilities.
  useEffect(() => {
    const bridge = getDesktop();
    if (!bridge) return;

    void bridge.cowork.setAuthToken(getAuthHeader());
    void bridge.cowork.getCapabilities().then(setCapabilities).catch(() => {});
    void bridge.cowork.getRecentFolders().then((r) => setRecentFolders(r.folders)).catch(() => {});
    void bridge.cowork.getStatus().then((status) => {
      if (!status.folderPath) return;
      const name = status.folderPath.split(/[\\/]/).filter(Boolean).pop() ?? status.folderPath;
      useCoworkStore.getState().setSession({
        deviceId: status.deviceId,
        folderName: name,
        folderPath: status.folderPath,
      });
      useCoworkStore.getState().setConnected(status.connected);
    });
  }, []);

  const setMode = useCallback((next: "chat" | "cowork") => {
    useCoworkStore.getState().setMode(next);
  }, []);

  const openFolder = useCallback(async () => {
    const bridge = getDesktop();
    if (!bridge) return;
    await bridge.cowork.setAuthToken(getAuthHeader());
    const result = await bridge.cowork.openFolder();
    if (result.canceled || !result.deviceId || !result.folderName) return;
    useCoworkStore.getState().setSession({
      deviceId: result.deviceId,
      folderName: result.folderName,
      folderPath: result.folderPath,
    });
    useCoworkStore.getState().setConnected(true);
    useCoworkStore.getState().setMode("cowork");
    // Re-assert autonomy + exec tier on connect. main.ts resets these to
    // defaults ("ask"/"native") on app restart and only learns of pill *changes*
    // — so without this the pill can read "Full" while the executor still gates
    // on "ask", prompting for approvals that should auto-pass.
    {
      const s = useCoworkStore.getState();
      void bridge.cowork.setAutonomy(s.autonomy).catch(() => {});
      void bridge.cowork.setExecTier(s.execTier).catch(() => {});
    }
    toast.success(`CoWork on “${result.folderName}” — the agent can now work this folder.`);
  }, []);

  const openRecent = useCallback(async (folderPath: string) => {
    const bridge = getDesktop();
    if (!bridge) return;
    await bridge.cowork.setAuthToken(getAuthHeader());
    const result = await bridge.cowork.openRecent(folderPath);
    if (!result.deviceId || !result.folderName) return;
    useCoworkStore.getState().setSession({
      deviceId: result.deviceId,
      folderName: result.folderName,
      folderPath: result.folderPath,
    });
    useCoworkStore.getState().setConnected(true);
    useCoworkStore.getState().setMode("cowork");
    // Re-assert autonomy + exec tier on reconnect (see openFolder note above).
    {
      const s = useCoworkStore.getState();
      void bridge.cowork.setAutonomy(s.autonomy).catch(() => {});
      void bridge.cowork.setExecTier(s.execTier).catch(() => {});
    }
  }, []);

  const closeFolder = useCallback(async () => {
    await getDesktop()?.cowork.closeFolder().catch(() => {});
    useCoworkStore.getState().clear();
  }, []);

  const setAutonomy = useCallback(async (level: Autonomy) => {
    await getDesktop()?.cowork.setAutonomy(level).catch(() => {});
    useCoworkStore.getState().setAutonomy(level);
  }, []);

  const setExecTier = useCallback(async (tier: ExecTier) => {
    await getDesktop()?.cowork.setExecTier(tier).catch(() => {});
    useCoworkStore.getState().setExecTier(tier);
  }, []);

  const undo = useCallback(async (relPath: string) => {
    await getDesktop()?.cowork.undo(relPath).catch(() => {});
    const fresh = await getDesktop()?.cowork.getChanges();
    if (fresh) useCoworkStore.getState().setChanges(fresh);
  }, []);

  const reveal = useCallback(async (relPath: string, isDir: boolean) => {
    await getDesktop()?.cowork.reveal(relPath, isDir).catch(() => {});
  }, []);

  return {
    desktop,
    mode,
    setMode,
    deviceId,
    folderName,
    folderPath,
    connected,
    autonomy,
    setAutonomy,
    execTier,
    setExecTier,
    capabilities,
    changes,
    undo,
    reveal,
    recentFolders,
    openFolder,
    openRecent,
    closeFolder,
  };
}
